// Hideo - router: one address per page (src/navigation.ts), each page a component (PAGES, shown by the RouterView in
// App.vue). app.switchTab() (src/features/app.ts) shows a page at once and updates the address with navigate(); on
// Back/Forward or a typed address the router calls app.showPage(). The legal pages (LegalPage.vue) are routes of their own,
// outside the page ids: they never change app.page (src/features/legal.ts for how they behave before a profile is chosen).
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { LEGAL_DOCS, OLD_PATHS, PAGE_PATHS, isPage, legalPath, type LegalDoc, type PageId } from './navigation'
import { LANGS, type Lang } from './i18n'
import { app } from './features/app'
import { afterLegalNavigation } from './features/legal'

declare module 'vue-router' {
  interface RouteMeta {
    /** A legal page (LegalPage.vue) */
    legal?: LegalDoc
  }
}

// Pages drawn by Vue (loaded on demand: each page is its own file in the build)
const PAGES: Record<PageId, RouteRecordRaw['component']> = {
  home: () => import('./pages/HomePage.vue'),
  ped: () => import('./pages/BoardPage.vue'),
  calendar: () => import('./pages/CalendarPage.vue'),
  timeline: () => import('./pages/TimelinePage.vue'),
  help: () => import('./pages/HelpPage.vue'),
  vault: () => import('./pages/HooksPage.vue'),
  keywords: () => import('./pages/KeywordsPage.vue'),
  published: () => import('./pages/PublishedPage.vue'),
  settings: () => import('./pages/SettingsPage.vue'),
  creators: () => import('./pages/CreatorsPage.vue'),
  sources: () => import('./pages/SourcesPage.vue'),
  saved: () => import('./pages/SavedPage.vue'),
  formats: () => import('./pages/FormatsPage.vue'),
  scripts: () => import('./pages/ScriptsPage.vue')
}
const LegalPage = () => import('./pages/LegalPage.vue')
/** Legal pages: /privacy (UI language), /en/privacy and /it/privacy (always that language); the old static addresses
 *  /privacy.html (once the Italian page: now the UI language) and /en/privacy.html redirect (without a history entry of
 *  their own) */
const legalRoute = (doc: LegalDoc, lang: Lang | null): RouteRecordRaw =>
  ({ name: lang ? `legal-${lang}-${doc}` : `legal-${doc}`, path: legalPath(doc, lang), component: LegalPage, props: { doc, lang }, meta: { legal: doc } })
const LEGAL_ROUTES: RouteRecordRaw[] = LEGAL_DOCS.flatMap(doc => [
  legalRoute(doc, null), ...LANGS.map(l => legalRoute(doc, l)),
  { path: `/${doc}.html`, redirect: legalPath(doc) },
  { path: `/en/${doc}.html`, redirect: legalPath(doc, 'en') }
])
const routes: RouteRecordRaw[] = [
  ...(Object.keys(PAGE_PATHS) as PageId[]).map(name => ({ name, path: PAGE_PATHS[name], component: PAGES[name] }) as RouteRecordRaw),
  ...LEGAL_ROUTES,
  // Pages moved to another section keep working at their old address
  ...Object.entries(OLD_PATHS).map(([path, to]) => ({ path, redirect: to })),
  { path: '/:unknown(.*)*', redirect: '/' }
]

export const router = createRouter({ history: createWebHistory(), routes })

// Navigations started by navigate() (the page is already shown): the router must not show it again. Counted per page,
// because two quick switchTab() calls start two navigations and the first one can finish after the second page is shown.
const pendingNav = new Map<string, number>()

router.afterEach(to => {
  const name = String(to.name)
  const pending = pendingNav.get(name) || 0
  if (pending) {
    if (pending === 1) pendingNav.delete(name)
    else pendingNav.set(name, pending - 1)
    return
  }
  // Back/Forward or a typed address. Before the profile choice the app is not ready: the address is used after it
  if (isPage(to.name) && app.booted && app.page !== to.name) app.showPage(to.name)
})
router.afterEach((to, from) => afterLegalNavigation(to, from))

/** Updates the address to the page already shown (app.switchTab) */
export function navigate(page: PageId, replace = false): void {
  if (router.currentRoute.value.name === page) return
  pendingNav.set(page, (pendingNav.get(page) || 0) + 1)
  const to = { name: page }
  ;(replace ? router.replace(to) : router.push(to)).catch(e => console.warn(e))
}
